// Stable hash helpers for block fingerprints and dependency signatures.
// Uses FNV-1a style hashing for fast deterministic IDs.
import type { LuisRenderLeaf } from '../render'

// Create deterministic hash from input text.
export function createStableHash(input: string): string {
  let hash = 0x811c9dc5
  // Fold each code unit into 32-bit FNV-1a hash state.
  for (let index = 0; index < input.length; index += 1) {
    hash ^= input.charCodeAt(index)
    hash = Math.imul(hash, 0x01000193)
  }

  // Return unsigned hash as compact base36 string.
  return (hash >>> 0).toString(36)
}

// Create block fingerprint from structural leaf content.
export function createLeafFingerprint(leaf: LuisRenderLeaf): string {
  const serialized = [
    leaf.atRules.join('\u001f'),
    leaf.selector,
    leaf.declarations.join('\u001f'),
  ].join('\u001e')
  // Return stable hash for selector/at-rule/declaration structure.
  return createStableHash(serialized)
}

// Create dependency hash from resolved variable signatures.
export function createDependencyHash(dependencies: string[], variableSignatures: Record<string, string>): string {
  const parts: string[] = []
  // Serialize each dependency with its current signature value.
  for (const dependency of dependencies) {
    const signature = variableSignatures[dependency] ?? ''
    parts.push(`${dependency}=${signature}`)
  }

  // Return stable hash for ordered dependency signature set.
  return createStableHash(parts.join('\u001f'))
}
